<script>
import {defineComponent, ref, onMounted, onUnmounted, computed, watch} from 'vue'
import {useCartStore} from "@js/store/CartStore.js";
import CustomCheckbox from "@js/components/Input/CustomCheckbox.vue";
import IconCheckMark from "@js/components/Icons/IconCheckMark.vue";
import {getAddressSuggestions} from "@js/yandex-api.ts";
import {checkDocument} from "@js/helpers.ts";
import {useDeliveryStore} from "@js/store/DeliveryStore.js";
import IconManipulatorCar1 from "@js/components/Icons/IconManipulatorCar1.vue";
import IconManipulatorCar2 from "@js/components/Icons/IconManipulatorCar2.vue";
import IconMixerCar1 from "@js/components/Icons/IconMixerCar1.vue";

export default defineComponent({
    name: "CartDelivery",
    components: {IconCheckMark, CustomCheckbox},

    setup() {
        const cartStore = useCartStore();
        const deliveryStore = useDeliveryStore();

        // Адрес + подсказки
        const query = ref(cartStore.form.delivery_address);
        const suggestions = ref([]);
        const dropdownRef = ref(null);
        let debounce = null;

        const fetchSuggestions = () => {
            clearTimeout(debounce);
            debounce = setTimeout(async () => {
                if ((query.value || '').length < 3) {
                    suggestions.value = [];
                    return;
                }
                suggestions.value = await getAddressSuggestions(query.value);
            }, 300);
        };

        const selectSuggestion = (value) => {
            query.value = value;
            cartStore.form.delivery_address = value;
            suggestions.value = [];
        };

        watch(query, (val) => {
            cartStore.form.delivery_address = val;
        });

        const handleClickOutside = (event) => {
            if (!dropdownRef.value?.contains(event.target)) {
                suggestions.value = [];
            }
        };

        // ТОЛЬКО манипулятор: берём список из DeliveryStore
        const vehiclesList = computed(() => deliveryStore.vehicles.manipulator);

        // Иконки
        const carIconMap = {
            '6m': IconManipulatorCar1,
            '12m': IconManipulatorCar2,
            'mixer': IconMixerCar1,
        };
        const carIconFor = (id) => carIconMap[id] ?? IconManipulatorCar1;

        onMounted(() => {
            if (checkDocument()) {
                document.addEventListener('click', handleClickOutside);
            }
            // дефолты для формы корзины под манипулятор:
            if (cartStore.form.vehicle !== '6m' && cartStore.form.vehicle !== '12m') {
                cartStore.form.vehicle = '6m';
            }
            cartStore.form.vehicle_type = 'manipulator'; // для обратной совместимости
        });

        onUnmounted(() => {
            if (checkDocument()) {
                document.removeEventListener('click', handleClickOutside);
            }
        });

        return {
            cartStore,
            query,
            suggestions,
            fetchSuggestions,
            selectSuggestion,
            dropdownRef,
            vehiclesList,
            carIconFor,
        };
    }
})
</script>

<template>
    <div class="bg-grayBg p-5 md:p-8 rounded-2xl space-y-6">
        <div>
            <h2 class="text-xl font-medium mb-3">Выберите способ доставки</h2>

            <div class="space-y-4">
                <label class="bg-white p-3 rounded border transition flex items-start gap-3 cursor-pointer"
                       :class="[
                 cartStore.form.delivery_type === 'pickup'
                   ? 'border-baseColor border-solid'
                   : 'border-gray-500 md:border-dashed md:hover:border-baseColor md:hover:border-solid'
               ]">
                    <input type="radio" v-model="cartStore.form.delivery_type" value="pickup"
                           class="mt-1 checked:border-baseColor checked:bg-baseColor max-md:active:border-baseColor focus:ring-baseColor"/>
                    <div>
                        <div class="font-medium">Самовывоз</div>
                        <div class="text-sm text-gray-500">(получение в пункте выдачи – бесплатно)</div>
                    </div>
                </label>

                <label class="bg-white p-3 rounded border transition flex items-start gap-3 cursor-pointer"
                       :class="[
                 cartStore.form.delivery_type === 'inside_mkad'
                   ? 'border-baseColor border-solid'
                   : 'border-gray-500 md:border-dashed md:hover:border-baseColor max-md:active:border-baseColor md:hover:border-solid'
               ]">
                    <input type="radio" v-model="cartStore.form.delivery_type" value="inside_mkad"
                           class="mt-1 checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"/>
                    <div class="flex justify-between w-full">
                        <div>
                            <div class="font-medium">Доставка в пределах МКАД</div>
                            <div class="text-sm text-gray-500">
                                Срок и стоимость определяются после оформления заказа — мы перезвоним.
                            </div>
                        </div>
                    </div>
                </label>

                <label class="bg-white p-3 rounded border transition flex items-start gap-3 cursor-pointer"
                       :class="[
                 cartStore.form.delivery_type === 'outside_mkad'
                   ? 'border-baseColor border-solid'
                   : 'border-gray-500 md:border-dashed md:hover:border-baseColor md:hover:border-solid'
               ]">
                    <input type="radio" v-model="cartStore.form.delivery_type" value="outside_mkad"
                           class="mt-1 checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"/>
                    <div class="flex flex-col w-full gap-2">
                        <div class="flex justify-between">
                            <div>
                                <div class="font-medium">Доставка за пределами МКАД</div>
                                <div class="text-sm text-gray-500">
                                    Срок и стоимость определяются после оформления заказа — мы свяжемся с вами.
                                </div>
                            </div>
                        </div>
                    </div>
                </label>

                <label class="bg-white p-3 rounded border transition flex items-start gap-3 cursor-pointer"
                       :class="[
                 cartStore.form.delivery_type === 'russia'
                   ? 'border-baseColor border-solid'
                   : 'border-gray-500 md:border-dashed md:hover:border-baseColor md:hover:border-solid'
               ]">
                    <input type="radio" v-model="cartStore.form.delivery_type" value="russia"
                           class="mt-1 checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"/>
                    <div>
                        <div class="font-medium">Доставка по России</div>
                        <div class="text-sm text-gray-500">
                            Срок и стоимость уточним после оформления — менеджер свяжется с вами.
                        </div>
                    </div>
                </label>
            </div>
        </div>

        <div v-if="cartStore.form.delivery_type !== 'pickup'" class="mt-3">
            <h2 class="text-xl font-medium">Укажите адрес доставки</h2>
            <div ref="dropdownRef" class="relative mb-4">
                <input v-model="query" @input="fetchSuggestions" type="text"
                       placeholder="Адрес доставки" autocomplete="off"
                       class="w-full mt-3 px-4 py-2 mb-1 border border-gray-300 focus:border-baseColor focus:ring-baseColor rounded-md"/>
                <ul v-if="suggestions.length"
                    class="absolute z-10 w-full bg-white border rounded shadow mt-1 max-h-60 overflow-y-auto">
                    <li v-for="(suggestion, i) in suggestions" :key="i"
                        @click="selectSuggestion(suggestion.label)"
                        class="px-4 py-2 md:hover:bg-gray-100 cursor-pointer text-sm">
                        {{ suggestion.label }}
                    </li>
                </ul>
            </div>
        </div>

        <div v-if="cartStore.form.delivery_type !== 'pickup'" class="mt-3">
            <h2 class="text-xl font-medium mb-3">
                Выберите конфигурацию манипулятора
            </h2>

            <!-- только 6м и 12м -->
            <div class="mb-3">
                <div class="flex gap-4 overflow-x-auto pb-1 -mx-2 px-2 md:mx-0 md:px-0 md:grid md:grid-cols-5">
                    <div v-for="v in vehiclesList" :key="v.id"
                         class="flex-none w-[95px] text-center md:flex-1 md:w-auto snap-start">
                        <button
                            @click="cartStore.setVehicle(v.id)"
                            class="h-12 w-24 md:h-14 md:w-full rounded-md border flex items-center justify-center transition shadow-sm p-1 overflow-hidden"
                            :class="cartStore.form.vehicle === v.id ? 'border-black text-black' : 'border-gray-300 text-gray-400 hover:text-gray-600'"
                            :aria-pressed="cartStore.form.vehicle === v.id"
                            :title="`Макс. длина: ${v.maxLenM} м`">
                            <component :is="carIconFor(v.id)"
                                       class="h-full w-full"
                                       :class="cartStore.vehicle === v.id ? 'opacity-100' : 'opacity-70'"/>
                        </button>
                        <span class="block mt-2 text-xs md:text-sm"
                              :class="cartStore.form.vehicle === v.id ? 'font-semibold text-gray-900' : 'text-gray-400'">
                            {{ v.label }}
                        </span>
                    </div>
                </div>
            </div>

            <p class="text-xs text-gray-500">
                Конфигурация манипулятора влияет на итоговую стоимость и возможность разгрузки длинномера (6–12&nbsp;м).
            </p>
        </div>

        <div>
            <h2 class="text-xl font-medium mb-3">Выберите способ оплаты</h2>
            <div class="space-y-3">
                <label class="flex items-center gap-3 cursor-pointer font-medium">
                    <input type="radio" v-model="cartStore.form.payment_type" value="cash"
                           class="checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"/>
                    <span>Для Физ.лиц без счета</span>
                </label>
                <label class="flex items-center gap-3 cursor-pointer font-medium">
                    <input type="radio" v-model="cartStore.form.payment_type" value="card"
                           class="checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"/>
                    <span>Банковской картой</span>
                </label>
                <label class="flex items-center gap-3 cursor-pointer font-medium">
                    <input type="radio" v-model="cartStore.form.payment_type" value="invoice"
                           class="checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"/>
                    <span>Счёт для юр. лиц</span>
                </label>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
input[type="radio"],
input[type="checkbox"] {
    accent-color: #ef5b27;
}
</style>
